// contexts/SocketContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';
import useLogout from '../customHooks/useLogout';
import { socket } from './socket';
import { useAppContext } from '.';
import { IActiveUser } from '../globals/interfaces';
import { Socket } from 'socket.io-client';
import { useLocation } from 'react-router-dom';

const SocketContext = createContext<{
    socket: Socket;
    activeUsers: Record<string, IActiveUser>;
}>({
    socket: null,
    activeUsers: {}
});

export const useSocketContext = () => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocketContext must be used within SocketProvider');
    }
    return context;
};

export const SocketProvider = ({ children }) => {

    const location = useLocation();
    const { user } = useAppContext();
    const { customLogout } = useLogout();
    const [activeUsers, setActiveUsers] = useState<Record<string, IActiveUser>>({});

    useEffect(() => {
        const resolveActiveUsers = (users) => {
            const now = moment().format('HH:mm');
            setActiveUsers((prev) => {
                const copy = _.cloneDeep(prev);
                for (let key in copy) {
                    if (!users[key]) {
                        copy[key].online = false;
                        copy[key].status = `Last seen ${now}`;
                    }
                }

                for (let key in users) {
                    copy[key] = users[key];
                    copy[key].online = true;
                    copy[key].status = 'Active';
                }

                return copy;
            });
        };

        const terminationLogout = (s_email) => {
            if (user.s_email.toLowerCase() === s_email.toLowerCase()) {
                customLogout();
            }
        }

        socket.on('users', resolveActiveUsers);
        socket.on('TERMINATION_LOGOUT', terminationLogout);

        return () => {
            socket.off('users', resolveActiveUsers);
            socket.off('TERMINATION_LOGOUT', terminationLogout);
        };
    }, [user.s_email]);

    // Emit user data when it changes
    useEffect(() => {
        if (!socket.connected) {
            // console.warn("Socket is not connected yet — delaying emit");
            return;
        }

        if (user.s_email) {
            const socketUser = {
                displayName: user.displayName,
                s_email: user.s_email,
                s_unit: user.s_unit,
                path: location.pathname
            };
            socket.emit('addUpdateUser', socketUser);
        }
    }, [user.s_email, user.s_unit, location.pathname, socket.connected]);

    return (
        <SocketContext.Provider value={{ activeUsers, socket }}>
            {children}
        </SocketContext.Provider>
    );
};

